import styles from '../templates/styles.css?inline';
import authModalHtml from '../templates/auth-modal.html?raw';

const ICONS = {
  lock: `<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`,
  smartphone: `<svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>`,
  eye: `<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>`,
  eyeOff: `<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>`
};

function getEmailError(value) {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "Email is required.";
  if (trimmedValue.length > 254) return "Email must be 254 characters or fewer.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) return "Enter a valid email address.";
  return "";
}

function validateFirstName(value) {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "First name is required.";
  if (trimmedValue.length < 2) return "Must be at least 2 characters.";
  if (trimmedValue.length > 40) return "Must be 40 characters or fewer.";
  if (!/[A-Za-z]/.test(trimmedValue)) return "Enter a valid name.";
  return "";
}

function validateLastName(value) {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "Last name is required.";
  if (trimmedValue.length < 2) return "Must be at least 2 characters.";
  if (trimmedValue.length > 40) return "Must be 40 characters or fewer.";
  if (!/[A-Za-z]/.test(trimmedValue)) return "Enter a valid name.";
  return "";
}

export class AuthModal {
  constructor(sdk, hostElement) {
    this.sdk = sdk;
    this.host = hostElement;
    this.visible = false;
    this.callback = null;

    // View state: 'login', 'signup', 'otp', 'success'
    this.view = 'login';
    this.status = '';
    this.isLoading = false;

    // OTP verification specific state (shared by login + signup flows)
    this.otpFlow = null; // 'login' | 'signup'
    this.activationCode = null;
    this.referenceId = null;
    this.maskedDestination = '';
    this.otpValue = '';

    // Inputs form data
    this.loginData = { email: '' };
    this.signupData = { firstName: '', lastName: '', email: '', acceptTerms: false };

    // Validation errors
    this.errors = { email: '', firstName: '', lastName: '', acceptTerms: '' };

    // Initialize Shadow DOM
    this.shadowRoot = this.host.attachShadow({ mode: 'open' });

    // Ensure the host wrapper stays on top of the quote drawer modal overlay
    this.host.style.position = 'relative';
    this.host.style.zIndex = '9999999999';

    this.init();
    // Register with global SDK
    this.sdk.registerAuthModal(this);
  }

  init() {
    // Append styles
    const styleEl = document.createElement('style');
    styleEl.textContent = styles;
    this.shadowRoot.appendChild(styleEl);

    // Create container
    this.container = document.createElement('div');
    this.shadowRoot.appendChild(this.container);

    this.render();
  }

  show(callback) {
    this.visible = true;
    this.callback = callback;
    this.status = '';
    this.view = 'login';
    this.isLoading = false;
    this.otpFlow = null;
    this.activationCode = null;
    this.referenceId = null;
    this.maskedDestination = '';
    this.otpValue = '';
    this.errors = { email: '', firstName: '', lastName: '', acceptTerms: '' };

    if (!document.body.dataset.authScrollLocked) {
      document.body.dataset.authScrollLocked = 'true';
      this._prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    this.render();
  }

  hide(success = false) {
    this.visible = false;
    if (document.body.dataset.authScrollLocked) {
      delete document.body.dataset.authScrollLocked;
      document.body.style.overflow = this._prevOverflow || '';
    }
    this.render();
    if (this.callback) {
      this.callback(success);
      this.callback = null;
    }
  }

  getTemplate(templateId) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(authModalHtml, 'text/html');
    const template = doc.getElementById(templateId);
    return template ? template.innerHTML : '';
  }

  injectIcons(htmlString) {
    return htmlString
      .replace(/__ICON_LOCK__/g, ICONS.lock)
      .replace(/__ICON_SMARTPHONE__/g, ICONS.smartphone);
  }

  // Handle successful login persistence matching Next.js app logic
  async handleSuccessfulAuth(authData) {
    localStorage.setItem('storage_token', JSON.stringify(authData));
    const token = authData.token || authData.authCode;
    if (token) {
      localStorage.setItem('session_token', token);
      document.cookie = `session_token=${token}; path=/; max-age=86400; SameSite=Lax; Secure`;
    }
    if (authData.refreshToken) {
      localStorage.setItem('refresh_token', authData.refreshToken);
    }
    if (authData.authCode) {
      localStorage.setItem('auth_code', authData.authCode);
    }

    if (authData.token) {
      try {
        const response = await fetch('https://dev-api.shipdeez.com/api/v1/Auth/me', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({})
        });
        const meResponse = await response.json();

        if (meResponse && (meResponse.responseCode === 200 || meResponse.responseCode === 201) && meResponse.data) {
          const meResult = meResponse.data.result || meResponse.data;
          const userSession = meResult.UserSession;
          const allPermissions = meResult.roles?.flatMap(role =>
            role.Permissions?.filter(p => p.IsGranted).map(p => p.PermissionId) || []
          ) || [];
          localStorage.setItem('user_info', JSON.stringify(userSession));
          localStorage.setItem('user_permissions', JSON.stringify(allPermissions));
        }
      } catch (meError) {
        console.error("Failed to populate user info on SDK login:", meError);
      }
    }

    if (authData.tenantSelected) {
      document.cookie = `selected_tenant=${this.sdk.tenantId || ""}; path=/; max-age=86400; SameSite=Lax; Secure`;
      localStorage.setItem('selected_tenant', this.sdk.tenantId || "");
    }

    this.sdk.setAuth(true);
    this.view = 'success';
    this.render();
    setTimeout(() => {
      this.hide(true);
    }, 1500);
  }

  async handleLoginSubmit(e) {
    e.preventDefault();
    this.status = '';

    const emailErr = getEmailError(this.loginData.email);
    if (emailErr) {
      this.errors.email = emailErr;
      this.status = "Please correct the highlighted fields and try again.";
      this.render();
      return;
    }

    this.isLoading = true;
    this.render();

    try {
      const response = await fetch('https://dev-api.shipdeez.com/api/v1/Auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: this.loginData.email,
          tenantCode: this.sdk.tenantId || undefined
        })
      });

      const resJson = await response.json();

      if ((resJson.responseCode === 200 || resJson.responseCode === 201) && resJson.data) {
        const loginData = resJson.data.login || resJson.data.result?.login || resJson.data.result;
        if (loginData) {
          this.otpFlow = 'login';
          this.activationCode = loginData.activationCode;
          this.referenceId = loginData.referenceId;
          this.maskedDestination = loginData.maskedDestination || '';
          this.otpValue = '';
          this.view = 'otp';
        } else {
          this.status = "Login succeeded, but the verification payload was empty.";
        }
      } else {
        const errMsg = resJson.errorMessages?.[0]?.errorText || "Login failed. Please check your details.";
        this.status = errMsg;
      }
    } catch (err) {
      this.status = err?.message || "An unexpected error occurred.";
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  async handleSignupSubmit(e) {
    e.preventDefault();
    this.status = '';

    const fnErr = validateFirstName(this.signupData.firstName);
    const lnErr = validateLastName(this.signupData.lastName);
    const emErr = getEmailError(this.signupData.email);
    const tcErr = !this.signupData.acceptTerms ? "You must accept the terms to continue." : "";

    if (fnErr || lnErr || emErr || tcErr) {
      this.errors = {
        firstName: fnErr,
        lastName: lnErr,
        email: emErr,
        acceptTerms: tcErr
      };
      this.status = "Please correct the highlighted fields and try again.";
      this.render();
      return;
    }

    this.isLoading = true;
    this.render();

    try {
      const response = await fetch('https://dev-api.shipdeez.com/api/v1/Auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: this.signupData.firstName,
          lastName: this.signupData.lastName,
          email: this.signupData.email,
          tenantCode: this.sdk.tenantId || undefined
        })
      });

      const resJson = await response.json();

      if ((resJson.responseCode === 200 || resJson.responseCode === 201) && resJson.data) {
        const signupData = resJson.data.signup || resJson.data.result?.signup || resJson.data.result;
        if (signupData) {
          this.otpFlow = 'signup';
          this.activationCode = signupData.activationCode;
          this.referenceId = signupData.referenceId;
          this.maskedDestination = signupData.maskedDestination || '';
          this.otpValue = '';
          this.view = 'otp';
        } else {
          this.status = "Signup succeeded, but the verification payload was empty.";
        }
      } else {
        const errMsg = resJson.errorMessages?.[0]?.errorText || "Signup failed. Please check your details.";
        this.status = errMsg;
      }
    } catch (err) {
      this.status = err?.message || "An unexpected error occurred.";
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  async handleOtpSubmit(e) {
    e.preventDefault();
    if (this.otpValue.length !== 6) return;
    this.status = '';
    this.isLoading = true;
    this.render();

    try {
      const verifyEndpoint = this.otpFlow === 'signup' ? 'Auth/signup-verify' : 'Auth/verify';
      const response = await fetch(`https://dev-api.shipdeez.com/api/v1/${verifyEndpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activationCode: this.activationCode,
          referenceId: this.referenceId,
          otp: this.otpValue
        })
      });

      const resJson = await response.json();
      if ((resJson.responseCode === 200 || resJson.responseCode === 201) && resJson.data) {
        const authData = resJson.data.auth || resJson.data.result?.auth || resJson.data.result;
        if (authData) {
          await this.handleSuccessfulAuth(authData);
        } else {
          this.status = "Verification succeeded, but authentication payload was empty.";
        }
      } else {
        const errMsg = resJson.errorMessages?.[0]?.errorText || "Verification failed. Please check your code.";
        this.status = errMsg;
      }
    } catch (err) {
      this.status = err?.message || "An unexpected error occurred.";
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  render() {
    if (!this.visible) {
      this.container.innerHTML = '';
      return;
    }

    // Initialize the main shell only if it is not already built in DOM to prevent overlay fade animation jerks
    let subviewRoot = this.shadowRoot.getElementById('auth-subview-root');
    if (!subviewRoot) {
      this.container.innerHTML = `
        <div class="auth-overlay-standalone">
          <div class="auth-card-standalone">
            <button type="button" class="auth-close-btn" id="btn-auth-close">✕</button>
            <div id="auth-subview-root"></div>
          </div>
        </div>
      `;
      this.shadowRoot.getElementById('btn-auth-close').addEventListener('click', () => this.hide(false));
      subviewRoot = this.shadowRoot.getElementById('auth-subview-root');
    }

    let subViewContent = '';
    if (this.view === 'login') {
      subViewContent = this.injectIcons(this.getTemplate('auth-login-template'));
    } else if (this.view === 'signup') {
      subViewContent = this.injectIcons(this.getTemplate('auth-signup-template'));
    } else if (this.view === 'otp') {
      subViewContent = this.injectIcons(this.getTemplate('auth-mfa-template'));
    } else if (this.view === 'success') {
      subViewContent = this.injectIcons(this.getTemplate('auth-success-template'));
    }

    subviewRoot.innerHTML = subViewContent;

    // Bind tab switchers
    const tabLogin = this.shadowRoot.getElementById('tab-login');
    const tabSignup = this.shadowRoot.getElementById('tab-signup');
    if (tabLogin) {
      tabLogin.addEventListener('click', () => {
        this.view = 'login';
        this.status = '';
        this.render();
      });
    }
    if (tabSignup) {
      tabSignup.addEventListener('click', () => {
        this.view = 'signup';
        this.status = '';
        this.render();
      });
    }

    // Bind status messaging if set
    const statusBox = this.shadowRoot.getElementById('auth-status-container');
    if (statusBox && this.status) {
      statusBox.innerHTML = `
        <div style="background: #fef2f2; border: 1px solid #fca5a5; color: #ef4444; padding: 10px; font-size: 12px; border-radius: 6px; margin-bottom: 14px;">
          ${this.status}
        </div>
      `;
    }

    // Bind Login view specific events & input configurations
    if (this.view === 'login') {
      const authForm = this.shadowRoot.getElementById('standalone-auth-form');
      if (authForm) {
        authForm.addEventListener('submit', this.handleLoginSubmit.bind(this));
      }

      const emailInput = this.shadowRoot.getElementById('auth-email');
      const submitText = this.shadowRoot.getElementById('login-submit-text');
      const submitBtn = authForm?.querySelector('button[type="submit"]');

      if (emailInput) {
        emailInput.value = this.loginData.email;
        emailInput.addEventListener('input', (e) => { this.loginData.email = e.target.value; });
        if (this.errors.email) {
          emailInput.style.borderColor = '#ef4444';
          emailInput.style.backgroundColor = '#fef2f2';
          this.shadowRoot.getElementById('error-email').innerText = this.errors.email;
        }
      }

      if (submitText && this.isLoading) submitText.innerText = 'Sending Code...';
      if (submitBtn && this.isLoading) submitBtn.disabled = true;
    }

    // Bind Signup view specific events & input configurations
    if (this.view === 'signup') {
      const signupForm = this.shadowRoot.getElementById('standalone-signup-form');
      if (signupForm) {
        signupForm.addEventListener('submit', this.handleSignupSubmit.bind(this));
      }

      const fNameInput = this.shadowRoot.getElementById('signup-firstname');
      const lNameInput = this.shadowRoot.getElementById('signup-lastname');
      const emailInput = this.shadowRoot.getElementById('signup-email');
      const termsCheck = this.shadowRoot.getElementById('signup-terms');
      const submitText = this.shadowRoot.getElementById('signup-submit-text');
      const submitBtn = signupForm?.querySelector('button[type="submit"]');

      if (fNameInput) {
        fNameInput.value = this.signupData.firstName;
        fNameInput.addEventListener('input', (e) => { this.signupData.firstName = e.target.value; });
        if (this.errors.firstName) {
          fNameInput.style.borderColor = '#ef4444';
          fNameInput.style.backgroundColor = '#fef2f2';
          this.shadowRoot.getElementById('error-firstname').innerText = this.errors.firstName;
        }
      }
      if (lNameInput) {
        lNameInput.value = this.signupData.lastName;
        lNameInput.addEventListener('input', (e) => { this.signupData.lastName = e.target.value; });
        if (this.errors.lastName) {
          lNameInput.style.borderColor = '#ef4444';
          lNameInput.style.backgroundColor = '#fef2f2';
          this.shadowRoot.getElementById('error-lastname').innerText = this.errors.lastName;
        }
      }
      if (emailInput) {
        emailInput.value = this.signupData.email;
        emailInput.addEventListener('input', (e) => { this.signupData.email = e.target.value; });
        if (this.errors.email) {
          emailInput.style.borderColor = '#ef4444';
          emailInput.style.backgroundColor = '#fef2f2';
          this.shadowRoot.getElementById('error-email').innerText = this.errors.email;
        }
      }
      if (termsCheck) {
        termsCheck.checked = this.signupData.acceptTerms;
        termsCheck.addEventListener('change', (e) => { this.signupData.acceptTerms = e.target.checked; });
        if (this.errors.acceptTerms) {
          this.shadowRoot.getElementById('error-terms').innerText = this.errors.acceptTerms;
        }
      }

      if (submitText && this.isLoading) submitText.innerText = 'Creating Account...';
      if (submitBtn && this.isLoading) submitBtn.disabled = true;
    }

    // Bind OTP verification events & labels (shared by login + signup flows)
    if (this.view === 'otp') {
      const mfaForm = this.shadowRoot.getElementById('mfa-verification-form');
      if (mfaForm) {
        mfaForm.addEventListener('submit', this.handleOtpSubmit.bind(this));
      }

      const titleText = this.shadowRoot.getElementById('mfa-title-text');
      const descText = this.shadowRoot.getElementById('mfa-desc-text');
      const otpInput = this.shadowRoot.getElementById('mfa-otp-input');
      const submitText = this.shadowRoot.getElementById('mfa-submit-text');
      const submitBtn = mfaForm?.querySelector('button[type="submit"]');

      if (titleText) titleText.innerText = "Verify Your Email";
      if (descText) {
        descText.innerText = this.maskedDestination
          ? `Enter the 6-digit code sent to ${this.maskedDestination}.`
          : "Enter the verification code sent to your email.";
      }

      if (otpInput) {
        otpInput.value = this.otpValue;
        otpInput.addEventListener('input', (e) => {
          this.otpValue = e.target.value.replace(/\D/g, "");
          otpInput.value = this.otpValue;

          if (submitBtn) {
            submitBtn.disabled = this.otpValue.length !== 6 || this.isLoading;
          }
        });
      }

      if (submitText && this.isLoading) submitText.innerText = 'Verifying...';
      if (submitBtn) submitBtn.disabled = this.otpValue.length !== 6 || this.isLoading;

      const btnMfaBack = this.shadowRoot.getElementById('btn-mfa-back');
      if (btnMfaBack) {
        btnMfaBack.addEventListener('click', () => {
          this.view = this.otpFlow === 'signup' ? 'signup' : 'login';
          this.status = '';
          this.render();
        });
      }
    }
  }
}
